import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Building2,
  Check,
  Info,
  KeyRound,
  Network,
  Users,
  X,
} from 'lucide-react';
import { useToast } from '../../../../../components/common/Toast';
import { useAccessTheme } from '../useAccessTheme';
import { CapabilityCheckList } from './ProviderBadges';
import EntraIcon from './EntraIcon';
import EntraConnectionStep from './EntraConnectionStep';
import EntraSyncStep from './EntraSyncStep';
import EntraSsoStep from './EntraSsoStep';
import EntraReviewStep from './EntraReviewStep';
import { finishEntraSetup } from './entraConnectionService';
import { EntraSection } from './EntraWizardUi';
import {
  ENTRA_WIZARD_STEPS,
  createDefaultEntraWizardState,
  toEntraProviderRecord,
  type EntraWizardState,
  type EntraWizardStep,
} from './entraWizardTypes';
import type { IdentityProviderRecord } from './identityProviderTypes';

interface EntraSetupModalProps {
  onClose: () => void;
  onFinish?: (provider: IdentityProviderRecord) => void;
  existing?: IdentityProviderRecord | null;
}

const STEP_TITLES: Record<EntraWizardStep, { title: string; subtitle: string }> = {
  1: {
    title: 'Connect Microsoft Entra ID',
    subtitle: 'Connect your Microsoft Entra organization to synchronize users and groups and optionally enable Single Sign-On.',
  },
  2: {
    title: 'Connection',
    subtitle: 'Connect Selfic to your Microsoft Entra tenant using an App Registration.',
  },
  3: {
    title: 'Directory Synchronization',
    subtitle: 'Choose which users, groups, and attributes to synchronize.',
  },
  4: {
    title: 'Single Sign-On',
    subtitle: 'Allow users to authenticate with Microsoft Entra ID using OpenID Connect.',
  },
  5: {
    title: 'Review Microsoft Entra ID Setup',
    subtitle: 'Confirm the connection, directory settings, and sign-in policy.',
  },
};

const EntraSetupModal: React.FC<EntraSetupModalProps> = ({ onClose, onFinish, existing }) => {
  const t = useAccessTheme();
  const { showToast } = useToast();
  const [step, setStep] = useState<EntraWizardStep>(1);
  const [finishing, setFinishing] = useState(false);
  const [state, setState] = useState<EntraWizardState>(() => createDefaultEntraWizardState(existing));

  const patch = (partial: Partial<EntraWizardState>) => setState((prev) => ({ ...prev, ...partial }));

  const canContinue = step !== 2 || (state.connected && Boolean(state.connectionId));

  const goNext = () => {
    if (!canContinue) return;
    setStep((prev) => (prev < 5 ? ((prev + 1) as EntraWizardStep) : prev));
  };

  const goBack = () => {
    if (step === 1) {
      onClose();
      return;
    }
    setStep((prev) => (prev > 1 ? ((prev - 1) as EntraWizardStep) : prev));
  };

  const goToStep = (next: EntraWizardStep) => {
    if (next > 2 && (!state.connected || !state.connectionId)) return;
    setStep(next);
  };

  const finish = async () => {
    if (!state.connectionId || finishing) return;
    setFinishing(true);
    try {
      const result = await finishEntraSetup(state.connectionId);
      showToast({ type: 'success', message: result.message });
      onFinish?.(toEntraProviderRecord(state, existing, result));
      onClose();
    } catch (err) {
      showToast({ type: 'error', message: err instanceof Error ? err.message : 'Unable to finish setup' });
    } finally {
      setFinishing(false);
    }
  };

  return createPortal(
    <div className={'fixed inset-0 flex items-center justify-center p-4 ' + t.overlay} style={{ zIndex: 200 }}>
      <div className={`rounded-xl shadow-xl w-full max-w-5xl h-[min(860px,92vh)] overflow-hidden border flex ${t.panel}`}>
        <aside className={`hidden md:flex w-64 shrink-0 flex-col border-r ${t.header}`}>
          <div className={`px-5 py-5 border-b ${t.border}`}>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center">
                <EntraIcon className="w-8 h-8" />
              </div>
              <div>
                <p className={`text-sm font-semibold ${t.title}`}>Microsoft Entra ID</p>
                <p className={`text-xs ${t.muted}`}>Identity Provider Setup</p>
              </div>
            </div>
          </div>
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {ENTRA_WIZARD_STEPS.map((item) => {
              const active = step === item.id;
              const complete = step > item.id;
              const locked = item.id > 2 && (!state.connected || !state.connectionId);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => goToStep(item.id)}
                  disabled={locked}
                  className={`w-full text-left rounded-lg px-3 py-2.5 ${locked ? 'opacity-50 cursor-not-allowed' : t.actionHover} ${active ? t.badgeAccent : ''}`}
                >
                  <div className="flex items-start gap-3">
                    <span
                      className={`mt-0.5 w-6 h-6 rounded-full text-xs font-semibold flex items-center justify-center shrink-0 ${
                        complete
                          ? 'bg-emerald-500 text-white'
                          : active
                            ? 'bg-indigo-600 text-white'
                            : t.isLight
                              ? 'bg-neutral-200 text-neutral-600'
                              : 'bg-neutral-800 text-gray-300'
                      }`}
                    >
                      {complete ? <Check className="w-3.5 h-3.5" /> : item.id}
                    </span>
                    <span>
                      <span className={`block text-sm font-medium ${t.title}`}>{item.title}</span>
                      <span className={`block text-xs mt-0.5 ${t.muted}`}>{item.subtitle}</span>
                    </span>
                  </div>
                </button>
              );
            })}
          </nav>
        </aside>

        <div className="flex-1 min-w-0 flex flex-col">
          <div className={`md:hidden px-5 py-3 border-b overflow-x-auto ${t.header}`}>
            <div className="flex items-center gap-2 min-w-max">
              {ENTRA_WIZARD_STEPS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => goToStep(item.id)}
                  disabled={item.id > 2 && (!state.connected || !state.connectionId)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium ${step === item.id ? t.badgeAccent : t.badge}`}
                >
                  {item.id}. {item.title}
                </button>
              ))}
            </div>
          </div>
          <div className={`flex items-start justify-between gap-3 px-5 py-4 border-b ${t.header}`}>
            <div>
              <h3 className={`text-lg font-semibold ${t.title}`}>{STEP_TITLES[step].title}</h3>
              <p className={`text-sm mt-1 ${t.muted}`}>{STEP_TITLES[step].subtitle}</p>
            </div>
            <button type="button" onClick={onClose} className={'p-2 rounded-lg shrink-0 ' + t.closeBtn}>
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-5 space-y-4">
            {step === 1 ? <OverviewStep /> : null}
            {step === 2 ? <EntraConnectionStep state={state} onChange={patch} /> : null}
            {step === 3 && state.connectionId ? <EntraSyncStep connectionId={state.connectionId} /> : null}
            {step === 4 && state.connectionId ? <EntraSsoStep connectionId={state.connectionId} onChange={patch} /> : null}
            {step === 5 && state.connectionId ? <EntraReviewStep connectionId={state.connectionId} /> : null}
          </div>

          <div className={`px-5 py-4 border-t flex items-center justify-between gap-2 ${t.footer}`}>
            {step === 1 ? (
              <button type="button" onClick={onClose} className={'px-4 py-2 rounded-lg text-sm font-medium ' + t.btnSecondary}>
                Cancel
              </button>
            ) : (
              <button type="button" onClick={goBack} className={'px-4 py-2 rounded-lg text-sm font-medium ' + t.btnSecondary}>
                Back
              </button>
            )}
            <div className="flex items-center gap-2">
              {step === 5 ? (
                <>
                  <button
                    type="button"
                    onClick={() => showToast({ type: 'info', message: 'Connection test is not available yet.' })}
                    className={'px-4 py-2 rounded-lg text-sm font-medium ' + t.btnSecondary}
                  >
                    Test Connection
                  </button>
                  <button
                    type="button"
                    onClick={finish}
                    disabled={finishing}
                    className={'px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 ' + t.btnPrimary}
                  >
                    {finishing ? 'Finishing...' : 'Finish Setup'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={goNext}
                  disabled={!canContinue}
                  className={'px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed ' + t.btnPrimary}
                >
                  Continue
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

function OverviewStep() {
  const t = useAccessTheme();
  const cards = [
    { title: 'Users', body: 'Import and keep users in sync', icon: Users, tone: t.isLight ? 'bg-indigo-50 text-indigo-600' : 'bg-indigo-900/30 text-indigo-300' },
    { title: 'Groups', body: 'Import and keep groups in sync', icon: Users, tone: t.isLight ? 'bg-emerald-50 text-emerald-600' : 'bg-emerald-900/30 text-emerald-300' },
    { title: 'Group memberships', body: 'Synchronize group memberships', icon: Network, tone: t.isLight ? 'bg-amber-50 text-amber-600' : 'bg-amber-900/30 text-amber-300' },
    { title: 'Department information', body: 'Sync department and organizational details', icon: Building2, tone: t.isLight ? 'bg-sky-50 text-sky-600' : 'bg-sky-900/30 text-sky-300' },
  ];

  return (
    <>
      <div className={`rounded-xl border p-4 ${t.tableShell}`}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0">
            <EntraIcon className="w-8 h-8" />
          </div>
          <div>
            <h4 className={`text-sm font-semibold ${t.title}`}>Microsoft Entra ID (Azure AD)</h4>
            <p className={`text-sm mt-1 ${t.body}`}>
              Securely connect to your Microsoft Entra tenant to automatically synchronize users, groups, and
              organizational information. You can also enable single sign-on for your users.
            </p>
          </div>
        </div>
      </div>

      <EntraSection title="Directory Synchronization" description="We will request permission to read the following information:">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.title} className={`rounded-lg border px-3 py-3 ${t.border}`}>
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${card.tone}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <p className={`text-sm font-medium mt-2 ${t.title}`}>{card.title}</p>
                <p className={`text-xs mt-0.5 ${t.muted}`}>{card.body}</p>
              </div>
            );
          })}
        </div>
        <div className="mt-4">
          <CapabilityCheckList items={['Users', 'Groups', 'Group memberships', 'Department information', 'Job titles', 'Manager information']} />
        </div>
      </EntraSection>

      <EntraSection title="Single Sign-On" description="Enable secure authentication for your users.">
        <div className={`rounded-lg border px-3 py-3 flex items-start gap-3 ${t.border}`}>
          <div className={'w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ' + t.iconBg}>
            <KeyRound className="w-4 h-4" />
          </div>
          <div>
            <p className={`text-sm font-medium ${t.title}`}>OpenID Connect (OIDC)</p>
            <p className={`text-xs mt-0.5 ${t.muted}`}>
              Configure OpenID Connect for modern and secure single sign-on with Microsoft Entra ID.
            </p>
          </div>
        </div>
        <div className="mt-3">
          <CapabilityCheckList items={['OpenID Connect (OIDC)']} />
        </div>
      </EntraSection>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <EntraSection title="Selfic will request permission to:">
          <CapabilityCheckList items={['Read users', 'Read groups', 'Read group memberships', 'Read basic organization information']} />
        </EntraSection>
        <EntraSection title="Selfic will not:">
          <CapabilityCheckList variant="deny" items={['Delete Microsoft users', 'Reset passwords', 'Modify Microsoft groups']} />
        </EntraSection>
      </div>

      <div className={`flex items-start gap-2 rounded-lg border px-3 py-2.5 ${t.border}`}>
        <Info className={`w-4 h-4 mt-0.5 shrink-0 ${t.accentText}`} />
        <p className={`text-sm ${t.body}`}>
          On the next step, enter your App Registration Tenant ID, Application (Client) ID, and client secret
          value. An Entra administrator must grant Application permissions and admin consent before Test
          Connection can succeed.
        </p>
      </div>
    </>
  );
}

export default EntraSetupModal;
